'use client';

import {
  BonusFilled,
  Close,
  ErrorFilled,
  InformationFilled,
  SuccessFilled,
  WarningFilled
} from '@ps/web-fusion-icons';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import { useFusionTheme, useIntl } from '../hooks';
import { TXTCLI_Close } from '../translations';

import styles from './InlineMessage.module.scss';

export const messageVariants = cva(styles.container, {
  variants: {
    variant: {
      info: styles.variantInfo,
      warning: styles.variantWarning,
      error: styles.variantError,
      success: styles.variantSuccess,
      bonus: styles.variantBonus
    }
  }
});

type MessageVariant = Exclude<VariantProps<typeof messageVariants>['variant'], null | undefined>;

export type InlineMessageProps = VariantProps<typeof messageVariants> & {
  variant?: MessageVariant;
  className?: string;
  title?: ReactNode;
  body: ReactNode;
  isDismissible?: boolean;
  onDismiss?: () => void;
  visible?: boolean;
  'aria-label'?: string;
};

const iconMap = {
  info: InformationFilled,
  warning: WarningFilled,
  error: ErrorFilled,
  success: SuccessFilled,
  bonus: BonusFilled
} as const satisfies Record<MessageVariant, React.FC<React.SVGProps<SVGElement>>>;

const InlineMessage = ({
  variant = 'info',
  className,
  title,
  body,
  onDismiss: onDismissCallback,
  isDismissible = true,
  visible = true,
  'aria-label': ariaLabel
}: InlineMessageProps) => {
  const { addThemeClassName } = useFusionTheme();
  const { formatMessage } = useIntl();
  const descriptionId = useId();
  const [isVisible, setVisible] = useState(visible);

  const onDismiss = () => {
    setVisible(false);
    onDismissCallback?.();
  };

  return (
    isVisible && (
      <div
        aria-label={ariaLabel}
        aria-describedby={descriptionId}
        className={addThemeClassName(messageVariants({ variant }), className)}
      >
        <Icon className={styles.icon} icon={iconMap[variant]} aria-hidden />
        <div className={styles.message}>
          {title && <h3 className={styles.title}>{title}</h3>}
          <div id={descriptionId} className={styles.body}>
            {body}
          </div>
        </div>
        {isDismissible && (
          <IconButton
            className={styles.close}
            icon={Close}
            aria-label={formatMessage(TXTCLI_Close)}
            onClick={onDismiss}
          />
        )}
      </div>
    )
  );
};

export default InlineMessage;
